import React from "react";
import { Link } from "react-router-dom";
import { formatINR, imgUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { Heart } from "lucide-react";

export function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const { has, toggle } = useWishlist();
  const wished = has(product.product_id);
  const discount = product.compare_at_price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  return (
    <article
      className="product-card group animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`product-card-${product.product_id}`}
    >
      <Link to={`/product/${product.product_id}`} className="block">
        <div className="relative overflow-hidden bg-[#f5f3ef] aspect-[3/4]">
          <img
            src={imgUrl(product.images?.[0])}
            alt={product.name}
            className="product-card-img w-full h-full object-cover"
            loading="lazy"
          />
          {product.new_arrival && (
            <span className="absolute top-3 left-3 bg-[#fcfbf8] text-[#7a1e2b] text-[10px] tracking-[0.2em] uppercase px-3 py-1 border border-[#c9a34a]">
              New
            </span>
          )}
          {discount > 0 && (
            <span className="absolute top-3 right-3 bg-[#7a1e2b] text-[#fcfbf8] text-[10px] tracking-[0.2em] uppercase px-3 py-1">
              -{discount}%
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product.product_id, product.name); }}
            className={`absolute ${discount > 0 ? "top-12" : "top-3"} right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm ${wished ? "bg-[#7a1e2b] text-[#fcfbf8]" : "bg-[#fcfbf8]/90 text-[#2b1b1c] hover:bg-[#7a1e2b] hover:text-[#fcfbf8]"}`}
            aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
            data-testid={`wishlist-btn-${product.product_id}`}
          >
            <Heart size={16} className={wished ? "fill-current" : ""} />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); add(product); }}
            className="absolute bottom-0 left-0 right-0 bg-[#7a1e2b] text-[#fcfbf8] text-[11px] tracking-[0.2em] uppercase py-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300"
            data-testid={`quick-add-${product.product_id}`}
          >
            Quick Add
          </button>
        </div>
        <div className="pt-4 pb-2">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#c9a34a] mb-1">{product.category}</div>
          <h3 className="font-serif text-lg text-[#2b1b1c] leading-snug">{product.name}</h3>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-[#7a1e2b] font-medium">{formatINR(product.price)}</span>
            {product.compare_at_price && (
              <span className="text-[#7c7273] line-through text-sm">{formatINR(product.compare_at_price)}</span>
            )}
          </div>
          {(product.variants || []).length > 1 && (
            <div className="mt-2 flex items-center gap-1.5" data-testid={`card-swatches-${product.product_id}`}>
              {product.variants.slice(0, 5).map((v) => (
                <span
                  key={v.variant_id}
                  className="w-3.5 h-3.5 rounded-full border border-[#e8e4db]"
                  style={{ backgroundColor: v.color_hex || "#c9a34a" }}
                  title={v.color}
                />
              ))}
              {product.variants.length > 5 && (
                <span className="text-[10px] text-[#7c7273] ml-1">+{product.variants.length - 5}</span>
              )}
            </div>
          )}
        </div>
      </Link>
    </article>
  );
}
